import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "@apollo/client";
import { useTheme } from "@mui/material/styles";
import { LoadingButton } from "@mui/lab";
import { AnnotationCommentEntityType } from "../../../../../utils/enums";

const labelSx = (theme) => ({
  display: "block",
  fontSize: "0.875rem",
  fontWeight: 600,
  color: theme.customColors?.text?.strong ?? "#374151",
  mb: 0.75,
  "& .required": { color: theme.customColors?.status?.error?.main ?? "#EF4444" },
});

const fieldSx = (theme) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
    "& fieldset": {
      border: "1px solid #D1D5DB",
    },
    "&:hover fieldset": {
      borderColor: "#9CA3AF",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#9CA3AF",
      borderWidth: "1px",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(156, 163, 175, 0.2)",
    },
    "& .MuiInputBase-input": {
      color: theme.customColors?.text?.primary ?? "#374151",
      fontSize: "0.9375rem",
      padding: "10px 14px",
      "&::placeholder": {
        color: "#9CA3AF",
        opacity: 1,
      },
    },
  },
});

function EvidenceRequestDialog({
  open,
  onClose,
  onSubmit,
  mode = "create",
  initialData = null,
  loading = false,
  controlsList = [],
  initialReason = "",
}) {
  const theme = useTheme();
  const [selectedControl, setSelectedControl] = useState(null);
  const [reason, setReason] = useState("");
  const controlsLoading = false;

  const controls = controlsList || [];

  useEffect(() => {
    if (!open) {
      setSelectedControl(null);
      setReason("");
    } else if (initialData) {
      // Edit mode - populate from initialData
      setReason(initialData.evidence_requested_reason || "");
      // Note: In edit mode, we might need to find the control from the data
      // For now, we'll leave it empty in edit mode as controls might not be easily mappable
      setSelectedControl(null);
    } else {
      // Create mode - auto-select if only one control
      if (controls.length === 1) {
        setSelectedControl(controls[0]);
      } else {
        setSelectedControl(null);
      }
      setReason(initialReason || "");
    }
  }, [open, initialData, controls, initialReason]);

  const handleClose = () => {
    if (!loading) {
      onClose?.();
    }
  };

  const handleSubmit = () => {
    if (!selectedControl || !reason.trim()) return;

    const data = {
      entity_type: AnnotationCommentEntityType.CONTROL,
      entity_id: selectedControl.auditControlId || selectedControl.id,
      evidence_requested_reason: reason.trim(),
    };

    if (mode === "edit" && initialData) {
      data.id = initialData.id;
    }

    onSubmit(data);
  };

  const canSubmit = selectedControl && reason.trim() && !loading;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "14px",
          border: `1px solid ${theme.customColors?.border?.subtle ?? "#E5E7EB"}`,
          boxShadow: theme.customColors?.shadow?.main ?? "0 2px 8px #00000010",
        },
      }}
    >
      <DialogTitle fontWeight="bold" sx={{ alignItems: "center" }}>
        {mode === "create" ? "Create Evidence Request" : "Edit Evidence Request"}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={loading}
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            color: theme.customColors?.text?.iconMuted ?? "#64748B",
            "&:hover": { backgroundColor: theme.customColors?.background?.hover ?? "#FAFAFA" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: theme.customColors?.background?.main ?? "#FFFFFF",
        }}
        dividers
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 0.5 }}>
          <Box>
            <Typography component="label" sx={labelSx(theme)}>
              Map to control <span className="required">*</span>
            </Typography>
            <Autocomplete
              options={controls}
              sx={{
                borderColor: theme.customColors?.border?.main,
                backgroundColor: theme.customColors?.background?.main,
                "& .MuiAutocomplete-option": {
                  justifyContent: "flex-start",
                },
              }}
              loading={controlsLoading}
              value={selectedControl}
              onChange={(_, value) => setSelectedControl(value)}
              getOptionLabel={(option) =>
                option ? (option.controlsTitle || option.controlsName || "") : ""
              }
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%",
                    py: 1,
                    gap: 0.25,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, color: theme.customColors.text.primary, width: "100%" }}>
                    {option.controlsTitle || option.controlsName || ""}
                  </Typography>
                  {option.controlsDescription && (
                    <Typography variant="caption" sx={{ color: theme.customColors.text.secondary, width: "100%" }}>
                      {option.controlsDescription}
                    </Typography>
                  )}
                </Box>
              )}
              isOptionEqualToValue={(opt, val) => {
                if (!opt || !val) return false;
                const name = (o) => (o?.controlsTitle || o?.controlsName || "").trim().toLowerCase();
                return name(opt) === name(val);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    controlsLoading ? "Loading..." : "Search and select control"
                  }
                  variant="outlined"
                  size="small"
                  sx={fieldSx(theme)}
                />
              )}
              disabled={loading || mode === "edit" || controls.length === 1}
            />
            <Typography
              variant="body2"
              sx={{
                mt: 0.75,
                fontSize: "0.8125rem",
                color: theme.customColors?.text?.secondary ?? "#6B7280",
              }}
            >
              Select the control(s) for which you need evidence.
            </Typography>
          </Box>

          <Box>
            <Typography component="label" sx={labelSx(theme)}>
              Description <span className="required">*</span>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Describe your evidence request in detail... "
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              variant="outlined"
              size="small"
              disabled={loading}
              sx={{
                ...fieldSx(theme),
                "& .MuiOutlinedInput-root .MuiInputBase-input": {
                  padding: "5px 0px 10px 0px",
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.customColors?.border?.subtle ?? "#E5E7EB"}`,
          backgroundColor: theme.customColors?.background?.main ?? "#FFFFFF",
        }}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            textTransform: "none",
            color: theme.customColors?.text?.secondary ?? "#6B7280",
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          loading={loading}
          sx={{
            fontWeight: 500,
            fontSize: "0.8125rem",
            px: 2,
            py: 0.75,
            borderRadius: 1,
            color: theme.customColors.button.text,
            "&:hover": {
              bgcolor: theme.palette._blue.dark,
            },
            "&:disabled": {
              bgcolor: theme.customColors.background.light,
              color: theme.customColors.text.disabled,
            },
          }}
        >
          {mode === "create" ? "Create Request" : "Update Request"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default EvidenceRequestDialog;
